import { useState, useEffect, useCallback } from "react";
import { supabase, Volunteer, SchoolClass, Student, School, logActivity } from "@/lib/supabase";
import { useAuth } from "@/lib/auth";
import { t } from "@/lib/i18n";
import { BookOpen, School as SchoolIcon, Brush, UserPlus, Trash2, Pencil, Users, Plus, Clock, Lock } from "lucide-react";
import ExcelBulkUpload from "@/components/ExcelBulkUpload";

type SubTab = "academics" | "classes" | "housekeeping";
type AttStatus = "present" | "absent" | "sick" | "leave";

const DEPARTMENTS = [
  "Administration",
  "Teachers (Pre-School)",
  "Teachers (Primary)",
  "Psycho-Social",
  "Media",
  "School Safety",
];

const LOCK_HOURS = 10;
const LOCK_MS = LOCK_HOURS * 60 * 60 * 1000;

interface AcademicsProps {
  initialAttendanceTarget?: { classId: string; level: "preschool" | "primary" | "secondary"; shift: "morning" | "afternoon"; schoolId?: string } | null;
  onConsumeInitialTarget?: () => void;
}

export default function Academics({ initialAttendanceTarget, onConsumeInitialTarget }: AcademicsProps = {}) {
  const { user } = useAuth();
  const [subTab, setSubTab] = useState<SubTab>("academics");

  const [schools, setSchools] = useState<School[]>([]);
  const [selectedSchoolId, setSelectedSchoolId] = useState("");

  const [acadShift, setAcadShift] = useState<"morning" | "afternoon">("morning");
  const [acadDept, setAcadDept] = useState("Administration");
  const [newWorkerName, setNewWorkerName] = useState("");
  const [acadWorkers, setAcadWorkers] = useState<Volunteer[]>([]);

  const [classShift, setClassShift] = useState<"morning" | "afternoon">("morning");
  const [classLevel, setClassLevel] = useState<"preschool" | "primary">("preschool");
  const [newClassName, setNewClassName] = useState("");
  const [classes, setClasses] = useState<SchoolClass[]>([]);
  const [activeClassId, setActiveClassId] = useState("");
  const [teacherName, setTeacherName] = useState("");
  const [assistantName, setAssistantName] = useState("");
  const [newStudentName, setNewStudentName] = useState("");
  const [classStudents, setClassStudents] = useState<Student[]>([]);

  const [houseDepts, setHouseDepts] = useState<string[]>(["Sanitation", "Maintenance"]);
  const [activeHouseDept, setActiveHouseDept] = useState("Sanitation");
  const [newHouseDept, setNewHouseDept] = useState("");
  const [newHouseWorker, setNewHouseWorker] = useState("");
  const [houseWorkers, setHouseWorkers] = useState<Volunteer[]>([]);

  const [attendanceDate, setAttendanceDate] = useState(new Date().toISOString().split("T")[0]);
  const [attendanceMap, setAttendanceMap] = useState<Record<string, {
    status: AttStatus;
    justification?: "justified" | "not_justified";
    created_at?: string;
  }>>({});

  const isWeekend = (dateStr: string) => {
    const day = new Date(dateStr).getDay();
    return day === 0 || day === 6;
  };

  const isPersonLocked = (personId: string): boolean => {
    const rec = attendanceMap[personId];
    if (!rec?.created_at) return false;
    return Date.now() - new Date(rec.created_at).getTime() < LOCK_MS;
  };

  const hoursRemaining = (personId: string): number => {
    const rec = attendanceMap[personId];
    if (!rec?.created_at) return 0;
    const unlockAt = new Date(rec.created_at).getTime() + LOCK_MS;
    return Math.max(0, Math.ceil((unlockAt - Date.now()) / (60 * 60 * 1000)));
  };

  const fetchAcadWorkers = useCallback(async () => {
    let q = supabase.from("volunteers").select("*").eq("category", "academic").eq("shift", acadShift).eq("department", acadDept);
    if (selectedSchoolId) q = q.eq("school_id", selectedSchoolId);
    const { data } = await q.order("created_at", { ascending: false });
    if (data) setAcadWorkers(data as Volunteer[]);
  }, [acadShift, acadDept, selectedSchoolId]);

  const fetchClasses = useCallback(async () => {
    let q = supabase.from("classes").select("*").eq("shift", classShift).eq("level", classLevel);
    if (selectedSchoolId) q = q.eq("school_id", selectedSchoolId);
    const { data } = await q.order("name");
    if (data) {
      setClasses(data as SchoolClass[]);
      if (data.length > 0 && !activeClassId) setActiveClassId(data[0].id);
    }
  }, [classShift, classLevel, selectedSchoolId]);

  const fetchClassStudents = useCallback(async () => {
    if (!activeClassId) { setClassStudents([]); return; }
    const { data } = await supabase
      .from("students")
      .select("*")
      .eq("class_id", activeClassId)
      .order("full_name");
    if (data) setClassStudents(data as Student[]);

    const cls = (classes as SchoolClass[]).find((c) => c.id === activeClassId);
    if (cls) { setTeacherName(cls.teacher_name); setAssistantName(cls.assistant_name); }
  }, [activeClassId, classes]);

  const fetchHouseWorkers = useCallback(async () => {
    let q = supabase.from("volunteers").select("*").eq("category", "housekeeping").eq("housekeeping_dept", activeHouseDept);
    if (selectedSchoolId) q = q.eq("school_id", selectedSchoolId);
    const { data } = await q.order("created_at", { ascending: false });
    if (data) setHouseWorkers(data as Volunteer[]);
  }, [activeHouseDept, selectedSchoolId]);

  const fetchAttendance = useCallback(async (personIds: string[], date: string) => {
    if (personIds.length === 0 || !date) { setAttendanceMap({}); return; }
    const { data } = await supabase
      .from("attendance")
      .select("*")
      .eq("record_date", date)
      .in("person_id", personIds);
    if (data) {
      const map: Record<string, { status: AttStatus; justification?: "justified" | "not_justified"; created_at?: string }> = {};
      (data as any[]).forEach((r) => {
        if (r.person_id) map[r.person_id] = { status: r.status, justification: r.justification, created_at: r.created_at };
      });
      setAttendanceMap(map);
    }
  }, []);

  useEffect(() => {
    supabase.from("schools").select("*").order("name").then(({ data }) => {
      if (data) {
        setSchools(data as School[]);
        if (data.length > 0) setSelectedSchoolId((data[0] as School).id);
      }
    });
  }, []);
  useEffect(() => { fetchAcadWorkers(); }, [fetchAcadWorkers]);
  useEffect(() => { fetchClasses(); }, [fetchClasses]);
  useEffect(() => { fetchClassStudents(); }, [fetchClassStudents]);
  useEffect(() => { fetchHouseWorkers(); }, [fetchHouseWorkers]);

  // Deep-link from Student Management: jump straight to a specific class's
  // attendance roster instead of the default academics/housekeeping view.
  useEffect(() => {
    if (!initialAttendanceTarget) return;
    const { classId, level, shift: targetShift, schoolId } = initialAttendanceTarget;
    if (level !== "preschool" && level !== "primary") {
      // Secondary isn't handled by the Classes sub-tab in Academics.
      onConsumeInitialTarget?.();
      return;
    }
    if (schoolId) setSelectedSchoolId(schoolId);
    setSubTab("classes");
    setClassLevel(level);
    setClassShift(targetShift);
    setActiveClassId(classId);
    onConsumeInitialTarget?.();
  }, [initialAttendanceTarget, onConsumeInitialTarget]);

  useEffect(() => {
    if (subTab === "academics") fetchAttendance(acadWorkers.map((w) => w.id), attendanceDate);
  }, [acadWorkers, attendanceDate, subTab, fetchAttendance]);

  useEffect(() => {
    if (subTab === "classes") fetchAttendance(classStudents.map((s) => s.id), attendanceDate);
  }, [classStudents, attendanceDate, subTab, fetchAttendance]);

  useEffect(() => {
    if (subTab === "housekeeping") fetchAttendance(houseWorkers.map((w) => w.id), attendanceDate);
  }, [houseWorkers, attendanceDate, subTab, fetchAttendance]);

  useEffect(() => {
    const channel = supabase
      .channel("academics-realtime")
      .on("postgres_changes", { event: "*", schema: "public", table: "volunteers" }, () => { fetchAcadWorkers(); fetchHouseWorkers(); })
      .on("postgres_changes", { event: "*", schema: "public", table: "classes" }, () => fetchClasses())
      .on("postgres_changes", { event: "*", schema: "public", table: "students" }, () => fetchClassStudents())
      .on("postgres_changes", { event: "*", schema: "public", table: "attendance" }, () => {
        if (subTab === "academics") fetchAttendance(acadWorkers.map((w) => w.id), attendanceDate);
        else if (subTab === "classes") fetchAttendance(classStudents.map((s) => s.id), attendanceDate);
        else if (subTab === "housekeeping") fetchAttendance(houseWorkers.map((w) => w.id), attendanceDate);
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [fetchAcadWorkers, fetchHouseWorkers, fetchClasses, fetchClassStudents, fetchAttendance, subTab, acadWorkers, classStudents, houseWorkers, attendanceDate]);

  const addAcadWorker = async () => {
    if (!newWorkerName.trim()) return;
    await supabase.from("volunteers").insert({
      full_name: newWorkerName.trim(),
      department: acadDept,
      shift: acadShift,
      category: "academic",
      school_id: selectedSchoolId || null,
    });
    await logActivity("added volunteer", "volunteer", undefined, newWorkerName.trim(), selectedSchoolId, `${acadDept} / ${acadShift}`);
    setNewWorkerName("");
    fetchAcadWorkers();
  };

  const deleteAcadWorker = async (id: string) => {
    await supabase.from("volunteers").delete().eq("id", id);
    fetchAcadWorkers();
  };

  const addClass = async () => {
    if (!newClassName.trim()) return;
    const { data } = await supabase.from("classes").insert({
      name: newClassName.trim(),
      level: classLevel,
      shift: classShift,
      school_id: selectedSchoolId || null,
    }).select().single();
    await logActivity("created class", "class", data?.id, newClassName.trim(), selectedSchoolId, `${classLevel} / ${classShift}`);
    setNewClassName("");
    await fetchClasses();
    if (data) setActiveClassId(data.id);
  };

  const updateClassTeachers = async () => {
    if (!activeClassId) return;
    await supabase.from("classes").update({
      teacher_name: teacherName,
      assistant_name: assistantName,
    }).eq("id", activeClassId);
    fetchClasses();
  };

  const deleteClass = async () => {
    if (!activeClassId) return;
    await supabase.from("classes").delete().eq("id", activeClassId);
    setActiveClassId("");
    fetchClasses();
  };

  const addStudentToClass = async () => {
    if (!newStudentName.trim() || !activeClassId) return;
    const cls = classes.find((c) => c.id === activeClassId);
    await supabase.from("students").insert({
      full_name: newStudentName.trim(),
      level: cls?.level || classLevel,
      shift: cls?.shift || classShift,
      class_id: activeClassId,
      status: "approved",
      guardian_id: user?.id,
      school_id: selectedSchoolId || null,
    });
    await logActivity("added student to class", "student", undefined, newStudentName.trim(), selectedSchoolId, cls?.name || "");
    setNewStudentName("");
    fetchClassStudents();
  };

  const deleteStudent = async (id: string) => {
    await supabase.from("students").delete().eq("id", id);
    fetchClassStudents();
  };

  const addHouseDept = async () => {
    if (!newHouseDept.trim()) return;
    if (!houseDepts.includes(newHouseDept.trim())) {
      setHouseDepts([...houseDepts, newHouseDept.trim()]);
    }
    setActiveHouseDept(newHouseDept.trim());
    setNewHouseDept("");
  };

  const deleteHouseDept = async () => {
    await supabase
      .from("volunteers")
      .delete()
      .eq("category", "housekeeping")
      .eq("housekeeping_dept", activeHouseDept);
    const newDepts = houseDepts.filter((d) => d !== activeHouseDept);
    setHouseDepts(newDepts.length > 0 ? newDepts : ["Sanitation"]);
    setActiveHouseDept(newDepts[0] || "Sanitation");
  };

  const addHouseWorker = async () => {
    if (!newHouseWorker.trim()) return;
    await supabase.from("volunteers").insert({
      full_name: newHouseWorker.trim(),
      category: "housekeeping",
      housekeeping_dept: activeHouseDept,
      school_id: selectedSchoolId || null,
    });
    await logActivity("added housekeeping worker", "volunteer", undefined, newHouseWorker.trim(), selectedSchoolId, activeHouseDept);
    setNewHouseWorker("");
    fetchHouseWorkers();
  };

  const deleteHouseWorker = async (id: string) => {
    await supabase.from("volunteers").delete().eq("id", id);
    fetchHouseWorkers();
  };

  const markAttendance = async (
    personId: string,
    personName: string,
    personType: "student" | "volunteer",
    status: AttStatus,
    extra?: { gender?: string; class_name?: string; level?: string; department?: string; shift?: string }
  ) => {
    if (isWeekend(attendanceDate)) {
      alert(t("acad_weekend_alert"));
      return;
    }
    if (isPersonLocked(personId)) {
      return;
    }

    const nowIso = new Date().toISOString();
    setAttendanceMap((prev) => ({ ...prev, [personId]: { ...prev[personId], status, created_at: nowIso } }));

    const existing = await supabase
      .from("attendance")
      .select("id")
      .eq("record_date", attendanceDate)
      .eq("person_id", personId)
      .maybeSingle();

    if (existing.data) {
      await supabase.from("attendance").update({
        status,
        justification: status === "absent" ? attendanceMap[personId]?.justification : null,
      }).eq("id", existing.data.id);
    } else {
      await supabase.from("attendance").insert({
        record_date: attendanceDate,
        person_type: personType,
        person_id: personId,
        person_name: personName,
        status,
        gender: extra?.gender || null,
        class_name: extra?.class_name || null,
        level: extra?.level || null,
        department: extra?.department || null,
        shift: extra?.shift || null,
        recorded_by: user?.id,
        school_id: selectedSchoolId || null,
      });
      await logActivity(`marked ${status}`, "attendance", personId, personName, selectedSchoolId, attendanceDate);
    }
  };

  const setJustification = async (personId: string, justification: "justified" | "not_justified") => {
    if (isPersonLocked(personId)) return;
    setAttendanceMap((prev) => ({ ...prev, [personId]: { ...prev[personId], justification } }));

    const { data: existing } = await supabase
      .from("attendance")
      .select("id")
      .eq("record_date", attendanceDate)
      .eq("person_id", personId)
      .maybeSingle();

    if (existing) {
      await supabase.from("attendance").update({ justification }).eq("id", existing.id);
    }
  };

  const weekendWarning = isWeekend(attendanceDate);

  const renderAttRow = (
    id: string,
    name: string,
    label: string,
    personType: "student" | "volunteer",
    extra?: { gender?: string; class_name?: string; level?: string; department?: string; shift?: string },
    onDelete?: (id: string) => void,
    onEdit?: (id: string) => void
  ) => {
    const att = attendanceMap[id];
    const locked = isPersonLocked(id);
    const isPresent = att?.status === "present";
    const isAbsent = att?.status === "absent";
    const isSick = att?.status === "sick";
    const isLeave = att?.status === "leave";

    return (
      <div key={id} className="attendance-row">
        <div>
          <strong>{name}</strong>
          <span className="text-xs text-gray-400 ml-2">({label})</span>
          {locked && (
            <span className="inline-flex items-center gap-1 text-xs bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full ml-2">
              <Lock className="w-3 h-3" /> reopens in ~{hoursRemaining(id)}h
            </span>
          )}
        </div>
        <div className="flex gap-2 items-center flex-wrap">
          {onEdit && (
            <button className="btn-warning btn-sm" onClick={() => onEdit(id)} disabled={locked}>
              <Pencil className="w-3.5 h-3.5" /> {t("acad_btn_edit")}
            </button>
          )}
          {onDelete && (
            <button className="btn-danger btn-sm" onClick={() => onDelete(id)} disabled={locked}>
              <Trash2 className="w-3.5 h-3.5" /> {t("acad_btn_delete")}
            </button>
          )}
          <button
            className="att-btn-present disabled:opacity-40 disabled:cursor-not-allowed"
            style={isPresent ? { outline: "3px solid #000" } : {}}
            disabled={locked}
            onClick={() => markAttendance(id, name, personType, "present", extra)}
          >{t("acad_btn_present")}</button>
          <button
            className="att-btn-absent disabled:opacity-40 disabled:cursor-not-allowed"
            style={isAbsent ? { outline: "3px solid #000" } : {}}
            disabled={locked}
            onClick={() => markAttendance(id, name, personType, "absent", extra)}
          >{t("acad_btn_absent")}</button>
          <button
            className="px-3 py-1.5 rounded-md text-xs font-bold text-white bg-[#D97706] disabled:opacity-40 disabled:cursor-not-allowed"
            style={isSick ? { outline: "3px solid #000" } : {}}
            disabled={locked}
            onClick={() => markAttendance(id, name, personType, "sick", extra)}
          >Sick</button>
          <button
            className="px-3 py-1.5 rounded-md text-xs font-bold text-white bg-[#7C3AED] disabled:opacity-40 disabled:cursor-not-allowed"
            style={isLeave ? { outline: "3px solid #000" } : {}}
            disabled={locked}
            onClick={() => markAttendance(id, name, personType, "leave", extra)}
          >Leave</button>
        </div>
        {isAbsent && (
          <div className="w-full mt-2 p-2.5 bg-red-50 border border-dashed border-[#D32F2F] rounded-md flex gap-2.5 flex-col">
            <div className="flex gap-2.5">
              <button
                className={`att-sub-btn disabled:opacity-40 disabled:cursor-not-allowed ${att?.justification === "justified" ? "outline-2 outline-solid outline-[#2B2D42]" : ""}`}
                disabled={locked}
                onClick={() => setJustification(id, "justified")}
              >{t("acad_btn_justified")}</button>
              <button
                className={`att-sub-btn disabled:opacity-40 disabled:cursor-not-allowed ${att?.justification === "not_justified" ? "outline-2 outline-solid outline-[#2B2D42]" : ""}`}
                disabled={locked}
                onClick={() => setJustification(id, "not_justified")}
              >{t("acad_btn_not_justified")}</button>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="animate-fadeIn">
      <ExcelBulkUpload
        type="volunteer"
        schoolId={selectedSchoolId}
        onComplete={() => { fetchAcadWorkers(); fetchHouseWorkers(); }}
      />

      <div className="card">
        <div className="mb-5 pb-3 border-b-2 border-gray-200">
          <h2 className="text-[#1C3988] flex items-center gap-3 text-xl font-bold">
            <BookOpen className="w-6 h-6" /> {t("acad_title")}
          </h2>
        </div>
        <p className="text-gray-500 text-sm mb-5">{t("acad_desc")}</p>

        <div className="flex flex-wrap gap-4 items-end mb-5">
          <div className="form-group flex-1 min-w-[250px]">
            <label className="form-label">{t("lbl_select_school")}</label>
            <div className="relative">
              <SchoolIcon className="absolute left-3 top-3.5 w-5 h-5 text-gray-400" />
              <select className="form-input pl-10" value={selectedSchoolId} onChange={(e) => setSelectedSchoolId(e.target.value)}>
                <option value="">{t("lbl_all_schools")}</option>
                {schools.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
          </div>
        </div>

        <div className="flex flex-wrap gap-4 items-end mb-5">
          <div className="form-group">
            <label className="form-label">{t("acad_attendance_date")}</label>
            <input className="form-input" type="date" value={attendanceDate} onChange={(e) => setAttendanceDate(e.target.value)} />
          </div>
          {weekendWarning && (
            <div className="bg-amber-50 border border-amber-300 text-[#D97706] px-4 py-2 rounded-md text-sm font-semibold">
              {t("acad_weekend_warning")}
            </div>
          )}
        </div>

        <div className="flex gap-2.5 mb-6 flex-wrap">
          <button className="btn-cyan" onClick={() => setSubTab("academics")}>
            <BookOpen className="w-5 h-5" /> {t("acad_opt_morning")} / {t("acad_opt_afternoon")}
          </button>
          <button className="btn-primary" onClick={() => setSubTab("classes")}>
            <SchoolIcon className="w-5 h-5" /> {t("acad_opt_preschool")} / {t("acad_opt_primary")}
          </button>
          <button className="btn-cyan" onClick={() => setSubTab("housekeeping")}>
            <Brush className="w-5 h-5" /> {t("acad_title")}
          </button>
        </div>

        {subTab === "academics" && (
          <div>
            <h3 className="text-[#1C3988] mb-4 flex items-center gap-2 font-bold">
              <Clock className="w-5 h-5" /> {t("acad_title")}
            </h3>
            <div className="flex gap-5 mb-5 flex-wrap items-end">
              <div className="form-group flex-1 min-w-[200px]">
                <label className="form-label">{t("lbl_shift")}</label>
                <select className="form-input" value={acadShift} onChange={(e) => setAcadShift(e.target.value as any)}>
                  <option value="morning">{t("opt_shift_morning")}</option>
                  <option value="afternoon">{t("opt_shift_afternoon")}</option>
                </select>
              </div>
              <div className="form-group flex-1 min-w-[200px]">
                <label className="form-label">{t("lbl_department")}</label>
                <select className="form-input" value={acadDept} onChange={(e) => setAcadDept(e.target.value)}>
                  {DEPARTMENTS.map((d) => <option key={d} value={d}>{d}</option>)}
                </select>
              </div>
              <div className="form-group flex-2 min-w-[280px]">
                <label className="form-label">{t("ph_worker")}</label>
                <div className="flex gap-2">
                  <input className="form-input flex-1" type="text" value={newWorkerName} onChange={(e) => setNewWorkerName(e.target.value)} placeholder={t("ph_worker")} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addAcadWorker(); } }} />
                  <button className="btn-cyan" style={{ padding: "10px 15px" }} onClick={addAcadWorker}>
                    <UserPlus className="w-4 h-4" /> {t("btn_add")}
                  </button>
                </div>
              </div>
            </div>

            <div className="bg-[#F1F5F9] p-4 rounded-lg border border-[#CBD5E1]">
              <h4 className="text-[#1C3988] mb-3 font-bold">
                {t("acad_roster_shift")} ({acadShift === "morning" ? t("acad_opt_morning") : t("acad_opt_afternoon")})
              </h4>
              {acadWorkers.length === 0 ? (
                <p className="text-gray-400 italic py-2.5">{t("acad_no_workers")} {acadDept} {t("acad_no_workers2")} {acadShift} {t("acad_no_workers3")}</p>
              ) : (
                <div>
                  {acadWorkers.map((w) => renderAttRow(w.id, w.full_name, acadDept, "volunteer", { department: w.department || acadDept, shift: w.shift || acadShift }, deleteAcadWorker))}
                </div>
              )}
            </div>
          </div>
        )}

        {subTab === "classes" && (
          <div>
            <h3 className="text-[#7A2222] mb-4 flex items-center gap-2 font-bold">
              <Users className="w-5 h-5" /> {t("acad_title")}
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 mb-5">
              <div className="form-group">
                <label className="form-label">{t("lbl_shift")}</label>
                <select className="form-input" value={classShift} onChange={(e) => { setClassShift(e.target.value as any); setActiveClassId(""); }}>
                  <option value="morning">{t("opt_shift_morning")}</option>
                  <option value="afternoon">{t("opt_shift_afternoon")}</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">{t("lbl_class")}</label>
                <select className="form-input" value={classLevel} onChange={(e) => { setClassLevel(e.target.value as any); setActiveClassId(""); }}>
                  <option value="preschool">{t("opt_level_preschool")}</option>
                  <option value="primary">{t("opt_level_primary")}</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">{t("ph_classname")}</label>
                <div className="flex gap-2">
                  <input className="form-input flex-1" type="text" value={newClassName} onChange={(e) => setNewClassName(e.target.value)} placeholder={t("ph_classname")} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addClass(); } }} />
                  <button className="btn-cyan" style={{ padding: "10px 15px" }} onClick={addClass}>
                    <Plus className="w-4 h-4" /> {t("acad_btn_create")}
                  </button>
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">{t("lbl_class")}</label>
                <div className="flex gap-2">
                  <select className="form-input flex-1" value={activeClassId} onChange={(e) => setActiveClassId(e.target.value)}>
                    {classes.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                  <button className="btn-danger btn-sm" onClick={deleteClass}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-5">
              <div className="form-group">
                <label className="form-label">{t("ph_teacher")}</label>
                <input className="form-input" type="text" value={teacherName} onChange={(e) => setTeacherName(e.target.value)} placeholder={t("ph_teacher")} onBlur={updateClassTeachers} />
              </div>
              <div className="form-group">
                <label className="form-label">{t("ph_assistant")}</label>
                <input className="form-input" type="text" value={assistantName} onChange={(e) => setAssistantName(e.target.value)} placeholder={t("ph_assistant")} onBlur={updateClassTeachers} />
              </div>
            </div>

            <div className="bg-[#F1F5F9] p-4 rounded-lg border border-[#CBD5E1] mb-5">
              <h4 className="text-[#1C3988] mb-3 font-bold">{t("acad_h4_add_student")}</h4>
              <div className="flex gap-2.5 max-w-[500px]">
                <input className="form-input flex-1" type="text" value={newStudentName} onChange={(e) => setNewStudentName(e.target.value)} placeholder={t("ph_student")} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addStudentToClass(); } }} />
                <button className="btn-primary" onClick={addStudentToClass}>
                  <UserPlus className="w-4 h-4" /> {t("btn_add_student")}
                </button>
              </div>
            </div>

            <div className="bg-[#F8FAFC] p-4 rounded-lg border border-[#CBD5E1]">
              <h4 className="text-[#1C3988] mb-3 font-bold">
                {classes.find((c) => c.id === activeClassId)?.name || t("acad_student_register_none")}
              </h4>
              {classStudents.length === 0 ? (
                <p className="text-gray-400 italic py-2.5">{t("acad_no_students")}</p>
              ) : (
                <div>
                  {classStudents.map((s) => renderAttRow(
                    s.id,
                    s.full_name,
                    `${s.gender || "n/a"} | ${s.level || "n/a"}`,
                    "student",
                    { gender: s.gender || undefined, class_name: classes.find((c) => c.id === s.class_id)?.name, level: s.level || undefined, shift: s.shift || undefined },
                    deleteStudent
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {subTab === "housekeeping" && (
          <div>
            <h3 className="text-[#1C3988] mb-4 flex items-center gap-2 font-bold">
              <Brush className="w-5 h-5" /> {t("acad_title")}
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mb-5">
              <div className="form-group">
                <label className="form-label">{t("lbl_department")}</label>
                <select className="form-input" value={activeHouseDept} onChange={(e) => setActiveHouseDept(e.target.value)}>
                  {houseDepts.map((d) => <option key={d} value={d}>{d}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">{t("ph_dept")}</label>
                <div className="flex gap-2">
                  <input className="form-input" type="text" value={newHouseDept} onChange={(e) => setNewHouseDept(e.target.value)} placeholder={t("ph_dept")} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addHouseDept(); } }} />
                  <button className="btn-cyan btn-sm" onClick={addHouseDept}>{t("btn_add")}</button>
                  <button className="btn-danger btn-sm" onClick={deleteHouseDept}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">{t("ph_worker_h")}</label>
                <div className="flex gap-2 max-w-[500px]">
                  <input className="form-input flex-1" type="text" value={newHouseWorker} onChange={(e) => setNewHouseWorker(e.target.value)} placeholder={t("ph_worker_h")} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addHouseWorker(); } }} />
                  <button className="btn-cyan" style={{ padding: "10px 15px" }} onClick={addHouseWorker}>
                    <UserPlus className="w-4 h-4" /> {t("btn_add")}
                  </button>
                </div>
              </div>
            </div>

            <div className="bg-[#F1F5F9] p-4 rounded-lg border border-[#CBD5E1]">
              <h4 className="text-[#1C3988] mb-3 font-bold">{activeHouseDept}</h4>
              {houseWorkers.length === 0 ? (
                <p className="text-gray-400 italic py-2.5">{t("acad_no_house_workers")} {activeHouseDept} {t("acad_no_house_workers2")}</p>
              ) : (
                <div>
                  {houseWorkers.map((w) => renderAttRow(w.id, w.full_name, activeHouseDept, "volunteer", { department: activeHouseDept }, deleteHouseWorker))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
